import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppShell, Box, Group, Text, Button, ActionIcon } from "@mantine/core";
import { Outlet, Link, useNavigate } from "react-router-dom";
import {
  IconFileText,
  IconUserCircle,
  IconLogout,
  IconPackage,
  IconHelpCircle,
} from "@tabler/icons-react";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import { ErrorBoundary } from "../common/ErrorBoundary";
import {
  physicianPortalService,
  DeliveredToSubjectType,
} from "../../services/physicianPortal.service";

function pickPortalLabel(subjectTypes: DeliveredToSubjectType[]): string {
  if (subjectTypes.includes("physician")) {
    return "Physician Portal";
  }
  if (subjectTypes.includes("practice")) {
    return "Practice Portal";
  }
  if (subjectTypes.includes("surgery_center")) {
    return "Lab Portal";
  }
  return "Portal";
}

export function PortalLayout() {
  const { isAuthenticated, logout } = usePortalAuthStore();
  const navigate = useNavigate();

  const { data: identity } = useQuery({
    queryKey: ["portal", "identity"],
    queryFn: () => physicianPortalService.getIdentity(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const portalLabel = useMemo(
    () => pickPortalLabel(identity?.subjectTypes ?? []),
    [identity?.subjectTypes]
  );

  const handleLogout = () => {
    logout();
    navigate("/portal/login");
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

        @keyframes portalFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .portal-page-enter {
          animation: portalFadeIn 0.35s ease-out forwards;
        }
        @media (max-width: 600px) {
          .portal-nav-desktop { display: none !important; }
          .portal-nav-mobile  { display: flex !important; }
          .portal-label       { display: none !important; }
        }
        @media (min-width: 601px) {
          .portal-nav-mobile  { display: none !important; }
        }
        .portal-desktop-nav-link {
          font-family: 'IBM Plex Sans', sans-serif;
          color: #374151;
          font-size: 14px;
          text-decoration: none;
          padding-bottom: 2px;
          border-bottom: 2px solid transparent;
          transition: border-color 200ms ease, color 200ms ease;
        }
        .portal-desktop-nav-link:hover {
          border-bottom-color: #04119A;
          color: #04119A;
        }
      `}</style>
      <AppShell header={{ height: 64 }} padding={0}>
        <AppShell.Header
          style={{
            background: "#ffffff",
            borderBottom: "1px solid #e5e7f8",
            boxShadow: "none",
          }}
        >
          <Box
            style={{
              maxWidth: 1280,
              margin: "0 auto",
              padding: "0 24px",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Group gap={10} align="center">
              <Text
                component={Link}
                to="/portal/dashboard"
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 22,
                  fontWeight: 400,
                  color: "#04119A",
                  letterSpacing: "-0.5px",
                  textDecoration: "none",
                  lineHeight: 1,
                }}
              >
                GCP
              </Text>
              <Box
                style={{
                  width: 1,
                  height: 18,
                  background: "#d1d5e8",
                  margin: "0 2px",
                }}
              />
              <Text
                className="portal-label"
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 11,
                  color: "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                {portalLabel}
              </Text>
            </Group>

            {isAuthenticated && (
              <Group
                gap={6}
                className="portal-nav-mobile"
                style={{ display: "none" }}
                align="center"
              >
                <ActionIcon
                  component={Link}
                  to="/portal/dashboard"
                  variant="subtle"
                  color="gray"
                  size={44}
                  aria-label="Reports"
                >
                  <IconFileText size={22} />
                </ActionIcon>
                <ActionIcon
                  component={Link}
                  to="/portal/request-supplies"
                  variant="subtle"
                  color="gray"
                  size={44}
                  aria-label="Request Supplies"
                >
                  <IconPackage size={22} />
                </ActionIcon>
                <ActionIcon
                  component={Link}
                  to="/portal/help"
                  variant="subtle"
                  color="gray"
                  size={44}
                  aria-label="Contact / Help"
                >
                  <IconHelpCircle size={22} />
                </ActionIcon>
                <ActionIcon
                  component={Link}
                  to="/portal/account"
                  variant="subtle"
                  color="gray"
                  size={44}
                  aria-label="Account Settings"
                >
                  <IconUserCircle size={22} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={44}
                  onClick={handleLogout}
                  aria-label="Sign out"
                >
                  <IconLogout size={20} />
                </ActionIcon>
              </Group>
            )}

            {isAuthenticated && (
              <Group gap={28} className="portal-nav-desktop" align="center">
                <Text
                  component={Link}
                  to="/portal/dashboard"
                  className="portal-desktop-nav-link"
                >
                  Reports
                </Text>
                <Text
                  component={Link}
                  to="/portal/request-supplies"
                  className="portal-desktop-nav-link"
                >
                  Request Supplies
                </Text>
                <Text
                  component={Link}
                  to="/portal/help"
                  className="portal-desktop-nav-link"
                >
                  Contact / Help
                </Text>
                <Text
                  component={Link}
                  to="/portal/account"
                  className="portal-desktop-nav-link"
                >
                  Account Settings
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={handleLogout}
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    color: "#6b7280",
                    fontWeight: 500,
                    letterSpacing: "0.01em",
                  }}
                  styles={{
                    root: {
                      "&:hover": {
                        color: "#04119A",
                        background: "rgba(4, 17, 154, 0.04)",
                      },
                    },
                  }}
                >
                  Sign Out
                </Button>
              </Group>
            )}
          </Box>
        </AppShell.Header>

        <AppShell.Main
          style={{
            background: `
              radial-gradient(ellipse at 15% 30%, rgba(4, 17, 154, 0.06) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 70%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
              #fafbff
            `,
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Box className="portal-page-enter">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </Box>
        </AppShell.Main>
      </AppShell>
    </>
  );
}

export default PortalLayout;
